import { createSlice, createSelector } from '@reduxjs/toolkit';
import {fetchGptResponse, fetchGptResponseStream, fetchMetricToTargetResponse,
       streamChatResponse, deleteConversation
} from "../../modules/Inference/inferenceApi";


const gptApiSlice = createSlice({
    name: 'gptApi',
    initialState: {
        responses: {}, // Use an object keyed by UUID
        status: 'idle',
        activeConversationId: null,
        conversationTitles: {},
        chatContext: 'portfolios',  // only one for now
        selectedPortfolioId: null,  // default to null
        error: null
    },
    reducers: {
        clearResponses: (state) => {
            state.responses = {};
        },
        removeResponseById: (state, action) => {
            delete state.responses[action.payload];
        },
        setActiveConversation: (state, action) => {
          state.activeConversationId = action.payload;  // e.g. "general" or a portfolio ID
        },
        setChatContext: (state, action) => {
            state.chatContext = action.payload;
        },
        setSelectedPortfolio: (state, action) => {
          state.selectedPortfolioId = action.payload;
          if (state.chatContext !== 'portfolios') {
              state.chatContext = 'portfolios';
          }
        },
        clearConversationById: (state, action) => {
          const { id } = action.payload;
          if (state.responses[id]) {
            state.responses[id].messages = [];
            // If you prefer removing it entirely from `responses`, do `delete state.responses[id]`
          }
          if (state.activeConversationId === id) {
            state.activeConversationId = null;
          }
          if (state.conversationTitles[id]) {
            delete state.conversationTitles[id];
          }
        },
        setConversationTitle: (state, action) => {
          const { id, title } = action.payload;
          state.conversationTitles[id] = title;
        },

        updateResponseById: (state, action) => {
            const { id, data } = action.payload;
            if (state.responses[id]) {
                state.responses[id].data = data;
                state.responses[id].timestamp = new Date().toISOString();
            }
        },
        appendUserMessage: (state, action) => {
          const { id, groupId, userChunk } = action.payload;

          // If conversation doesn't exist, create it with the groupId
          if (!state.responses[id]) {
            state.responses[id] = {
              groupId: groupId || id, // Use provided groupId or fallback to id
              messages: [],
              status: 'streaming'
            };
          }

          // Ensure groupId consistency within the conversation
          if (!state.responses[id].groupId) {
            state.responses[id].groupId = groupId || id;
          }

          state.responses[id].messages.push({
            role: 'user',
            content: userChunk,
            timestamp: new Date().toISOString(),
            messageId: id  // Add unique message ID
          });
        },
        // 2) Append streaming chunks from GPT
        appendStreamResponse: (state, action) => {
          const { id, groupId, chunk } = action.payload;

          if (!state.responses[id]) {
            state.responses[id] = {
              groupId: groupId || null,
              messages: [],
              status: 'streaming'
            };
          }

          if (groupId) {
            state.responses[id].groupId = groupId;
          }

          const messages = state.responses[id].messages;
          const lastMessage = messages[messages.length - 1];

          if (lastMessage && lastMessage.role === 'assistant') {
            lastMessage.content += chunk;
          } else {
            // Create new assistant message with a messageId
            messages.push({
              role: 'assistant',
              content: chunk,
              messageId: `${id}_assistant`, // Unique messageId for assistant message
              timestamp: new Date().toISOString()
            });
          }
        },
        clearResponse: (state, action) => {
            const { id } = action.payload;
            if (state.responses[id]) {
                state.responses[id].data = ""; // Clear data if needed
            }
        },
        createNewConversation: (state, action) => {
          const { id, isPortfolio, portfolioId } = action.payload;

          // If this is a portfolio conversation, use portfolioId as groupId
          const groupId = isPortfolio ? portfolioId : id;

          // Create new conversation entry if it doesn't exist
          if (!state.responses[id]) {
            state.responses[id] = {
              messages: [],
              status: 'idle',
              groupId   // Always set the groupId
            };
          }

          state.activeConversationId = id;

          // For portfolio conversations, update portfolio state
          if (isPortfolio && portfolioId) {
            state.selectedPortfolioId = portfolioId;
          }
        },
        updateMessageContent: (state, action) => {
          const { conversationId, messageId, content } = action.payload;

          console.log("updateMessageContent", action.payload);

          // Find the conversation
          const conversation = state.responses[conversationId];
          if (!conversation) return;

          // Find message index
          const messageIndex = conversation.messages.findIndex(msg =>
            msg.messageId === messageId || // Check explicit messageId
            (msg.role === 'assistant' && msg.messageId === `${conversationId}_assistant`) // Check constructed messageId
          );

          if (messageIndex !== -1) {
            // Update the message content
            conversation.messages[messageIndex] = {
              ...conversation.messages[messageIndex],
              content: content,
              edited: true,
              editedAt: new Date().toISOString()
            };
          }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGptResponse.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchGptResponse.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.responses[action.payload.id] = action.payload;
            })
            .addCase(fetchMetricToTargetResponse.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.responses[action.payload.id] = action.payload;
            })
            .addCase(fetchGptResponse.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(fetchGptResponseStream.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchGptResponseStream.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (state.responses[action.payload.id]) {
                    state.responses[action.payload.id].status = "complete";
                }
            })
            .addCase(fetchGptResponseStream.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(deleteConversation.fulfilled, (state, action) => {
              state.status = 'succeeded';
              const { conversationId } = action.payload;

              // Remove the conversation from responses
              delete state.responses[conversationId];

              // Remove the conversation title
              delete state.conversationTitles[conversationId];

              // Reset active conversation if it was the deleted one
              if (state.activeConversationId === conversationId) {
                state.activeConversationId = null;
              }
            })
    }
});

const selectResponses = state => state.gptApi.responses;

export const selectResponseById = createSelector(
  [selectResponses, (state, responseId) => responseId],
  (responses, responseId) => responses[responseId]
);

export const selectConversationById = createSelector(
  [
    (state) => state.gptApi.responses,
    (state, id) => id
  ],
  (responses, id) => {
    return responses[id] || { messages: [] };
  }
);

export const selectAllConversations = (state) => {
  const { responses, conversationTitles } = state.gptApi;
  // Filter out conversations with no messages and map remaining ones
  return Object.keys(responses)
    .filter(id => responses[id].messages && responses[id].messages.length > 0)
    .map((id) => {
      const groupId = responses[id].groupId;
      const title = conversationTitles[id] || '(Untitled)';
      return { id, title, groupId };
    });
};

export const {
    clearResponses,
    removeResponseById,
    updateResponseById,
    setChatContext,
    setSelectedPortfolio,
    createNewConversation,
    setActiveConversation,
    setConversationTitle,
    clearConversationById,
    appendUserMessage,
    appendStreamResponse,
    clearResponse,
    updateMessageContent
} = gptApiSlice.actions;
export default gptApiSlice.reducer;